import { IconButton, Menu, MenuProps } from '@mui/material';
import { Omit } from 'lodash';
import React, { forwardRef, useImperativeHandle, useState } from 'react';

export interface IconMenuHandle {
	close: () => void;
}

export interface IconMenuProps {
	icon: React.ReactNode;
	children: React.ReactNode;
	id?: string;
	buttonTestId?: string;
	menuProps?: Omit<MenuProps, 'anchorEl' | 'open' | 'onClose' | 'id'>;
}

const IconMenu = forwardRef<IconMenuHandle, IconMenuProps>((props, ref) => {
	const {
		icon,
		children,
		id = 'icon-menu',
		buttonTestId = 'icon-menu-button',
		menuProps = {}
	} = props;

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);

	const handleOpen = React.useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			setAnchorEl(event.currentTarget);
		},
		[]
	);

	const handleClose = React.useCallback(() => {
		setAnchorEl(null);
	}, []);

	useImperativeHandle(ref, () => ({ close: handleClose }), [handleClose]);

	return (
		<>
			<IconButton
				onClick={handleOpen}
				size="small"
				aria-controls={open ? id : undefined}
				aria-haspopup="true"
				aria-expanded={open ? 'true' : undefined}
				data-testid={buttonTestId}
			>
				{icon}
			</IconButton>
			<Menu
				id={id}
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				{...menuProps}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
			>
				{children}
			</Menu>
		</>
	);
});

IconMenu.displayName = 'IconMenu';

IconMenu.defaultProps = {
	id: 'icon-menu',
	buttonTestId: 'icon-menu-button',
	menuProps: {}
};

export default IconMenu;
